import { useEffect, useState, Fragment } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, Section, StatusBadge, ApprovalTimeline } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const TYPES = ["Casual Leave", "Sick Leave", "Annual Leave", "Half Day", "WFH", "Other"];

export default function EmpLeave() {
  const [list, setList] = useState([]);
  const [balance, setBalance] = useState([]);
  const [open, setOpen] = useState(false);
  const [track, setTrack] = useState(null);
  const [form, setForm] = useState({ leave_type: "Casual Leave", from_date: "", to_date: "", days: 1, reason: "" });

  const load = async () => {
    const [l, b] = await Promise.all([api.get("/leave"), api.get("/leave/balance")]);
    setList(l.data); setBalance(b.data);
  };
  useEffect(() => { load(); }, []);

  const apply = async () => {
    try {
      await api.post("/leave", { ...form, days: parseFloat(form.days) });
      toast.success("Leave applied");
      setOpen(false); setForm({ leave_type: "Casual Leave", from_date: "", to_date: "", days: 1, reason: "" });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="emp-leave">
      <PageHeader overline="Time Off" title="Leave"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="apply-leave-btn" className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white">Apply for Leave</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle className="font-head">Apply for Leave</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} className="w-full h-10 border border-[#E5E5E5] px-2" data-testid="leave-type">
                  {TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="rounded-none" data-testid="leave-from" />
                  <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="rounded-none" data-testid="leave-to" />
                </div>
                <Input type="number" step="0.5" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} placeholder="Days" className="rounded-none" data-testid="leave-days" />
                <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason" className="rounded-none" data-testid="leave-reason" />
                <Button onClick={apply} data-testid="submit-leave" className="w-full rounded-none bg-[#FF5500] hover:bg-[#E64D00]">Submit</Button>
              </div>
            </DialogContent>
          </Dialog>
        } />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {balance.map((b) => (
          <div key={b.type} className="bg-white border border-[#E5E5E5] p-4">
            <div className="eyebrow">{b.type}</div>
            <div className="font-head text-3xl font-light tracking-tighter mt-2">{b.remaining}<span className="text-neutral-400 text-base">/{b.allocated}</span></div>
            <div className="text-xs text-neutral-500 mt-1">Used {b.used}</div>
          </div>
        ))}
      </div>
      <Section title="Leave History">
        <table className="w-full table-dense">
          <thead>
            <tr className="border-b-2 border-[#1F4E8C] text-left">
              <th className="eyebrow">Type</th><th className="eyebrow">From</th><th className="eyebrow">To</th>
              <th className="eyebrow">Days</th><th className="eyebrow">Reason</th><th className="eyebrow">Status</th><th className="eyebrow">Track</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-neutral-500">No leave applied yet</td></tr>}
            {list.map((l) => (
              <Fragment key={l.id}>
                <tr className="border-b border-[#F0F0EE]">
                  <td>{l.leave_type}</td><td>{l.from_date}</td><td>{l.to_date}</td>
                  <td>{l.days}</td><td className="max-w-[300px] truncate">{l.reason}</td>
                  <td><StatusBadge status={l.status} /></td>
                  <td>
                    <button onClick={() => setTrack(track === l.id ? null : l.id)} data-testid={`track-${l.id}`}
                      className="text-xs font-semibold text-[#1F4E8C] underline underline-offset-2">
                      {track === l.id ? "Hide" : "Track"}
                    </button>
                  </td>
                </tr>
                {track === l.id && (
                  <tr className="border-b border-[#F0F0EE] bg-[#F7F7F5]">
                    <td colSpan={7} className="py-4 px-4">
                      <div className="eyebrow mb-3 text-neutral-500">Approval timeline</div>
                      <ApprovalTimeline events={l.events} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}
